import { axiosClient } from "@lib/services/axios";
import { ENDPOINTS } from "@lib/constants/general";
import { z } from "zod";
import {
  BindingResponse,
  BindingType,
  DeleteBindingResponse,
} from "@lib/types/binding";

export const bindingSchema = z
  .object({
    binding_type: z.string().min(1, "Binding type is required"),

    driver_id: z.string().nullable().optional(),
    driver_group_id: z.number().optional(),

    unit_id: z.number().optional(),
    unit_group_id: z.number().optional(),

    start_datetime: z.date().optional(),
    end_datetime: z.date().nullable().optional(),

    schedules: z
      .array(
        z.object({
          recurring_type: z.string(),
          start_time: z.string().min(1, "Start time is required"),
          end_time: z.string().min(1, "End time is required"),
          day_of_month: z.number().optional(),
          days_of_week: z.array(z.number().int()).optional(),
        })
      )
      .optional(),
  })
  .superRefine((data, ctx) => {
    // Ensure either driver_id or driver_group_id is provided, but not both or neither
    const hasDriverId = !!data.driver_id;
    const hasDriverGroupId = !!data.driver_group_id;
    if (hasDriverId === hasDriverGroupId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Provide either driver_id or driver_group_id, but not both or neither.",
        path: ["driver_id"],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Provide either driver_id or driver_group_id, but not both or neither.",
        path: ["driver_group_id"],
      });
    }

    // Ensure either unit_id or unit_group_id is provided, but not both or neither
    const hasUnitId = !!data.unit_id;
    const hasUnitGroupId = !!data.unit_group_id;
    if (hasUnitId === hasUnitGroupId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Provide either unit_id or unit_group_id, but not both or neither.",
        path: ["unit_id"],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Provide either unit_id or unit_group_id, but not both or neither.",
        path: ["unit_group_id"],
      });
    }

    // Start datetime validation for 'one_time' and 'expiring' bindings
    if (
      (data.binding_type === "one_time" || data.binding_type === "expiring") &&
      !data.start_datetime
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          data.binding_type === "one_time"
            ? "Start date is required for the selected binding type"
            : "Please specify duration for expiring binding",
        path: ["start_datetime"],
      });
    }

    // Ensure schedules are provided for recurring bindings
    if (
      data.binding_type === "recurring" &&
      (!data.schedules || data.schedules.length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please configure schedules for recurring binding",
        path: ["schedules"],
      });
    }

    // Ensure end_datetime is required for 'expiring' bindings
    if (data.binding_type === "expiring" && !data.end_datetime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "End date is required for the selected binding type",
        path: ["end_datetime"],
      });
    }

    // Ensure end_datetime is not before start_datetime
    if (
      data.end_datetime &&
      data.start_datetime &&
      data.end_datetime < data.start_datetime
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "End date cannot be earlier than start date",
        path: ["end_datetime"],
      });
    }
  });

export type BindingFormData = z.infer<typeof bindingSchema>;

// Zod schema for form validation
export const updateBindingSchema = z
  .object({
    binding_type: z.string().min(1, "Binding type is required"),
    start_datetime: z.date().optional(), // Make optional by default
    end_datetime: z.date().nullable().optional(),
    schedules: z
      .array(
        z.object({
          recurring_type: z.string(),
          start_time: z.string().min(1, "Start time is required"),
          end_time: z.string().min(1, "End time is required"),
          day_of_month: z.number().optional(),
          days_of_week: z.array(z.number().int()).optional(),
        })
      )
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (
      (data.binding_type === "one_time" || data.binding_type === "expiring") &&
      !data.start_datetime
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          data.binding_type === "one_time"
            ? "Start date is required for the selected binding type"
            : "Please specify duration for expiring binding",
        path: ["start_datetime"],
      });
    }

    if (
      data.binding_type === "recurring" &&
      (!data.schedules || data.schedules.length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please configure schedules for recurring binding",
        path: ["schedules"],
      });
    }

    if (data.binding_type === BindingType.EXPIRING && !!data.end_datetime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "End date is required for the selected binding type",
        path: ["end_datetime"],
      });
    }

    if (
      data.end_datetime &&
      data.start_datetime &&
      data.end_datetime < data.start_datetime
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "End date cannot be earlier than start date",
        path: ["end_datetime"],
      });
    }
  });
export type UpdateBindingFormData = z.infer<typeof updateBindingSchema>;

export const createBinding = async (
  createBindingParams: BindingFormData
): Promise<BindingResponse> => {
  const { data } = await axiosClient.post(
    ENDPOINTS.BASE_BINDING,
    createBindingParams
  );
  return data;
};

export const updateBinding = async ({
  bindingId,
  updateBindingParams,
}: {
  bindingId: string;
  updateBindingParams: UpdateBindingFormData;
}): Promise<BindingResponse> => {
  const { data } = await axiosClient.put(
    `${ENDPOINTS.BASE_BINDING}/${bindingId}`,
    updateBindingParams
  );
  return data;
};

export const deleteBinding = async (
  bindingId: string
): Promise<DeleteBindingResponse> => {
  const { data } = await axiosClient.delete(
    `${ENDPOINTS.BASE_BINDING}/${bindingId}`
  );

  return data;
};
